import { FC, memo, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';

import { useWindowDimensions } from '@hooks/useWindowDimensions';
import theme from 'theme';
import Modal from '@components/modal';

const Container = styled.div<{ $containerWidth: number }>`
  width: ${({ $containerWidth }) => `${$containerWidth}px`};
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  justify-content: center;
`;

const ColorButton = styled.button<{
  $color: string;
  $isSelected: boolean;
  $itemSize: number;
}>`
  width: ${({ $itemSize }) => `${$itemSize}px`};
  height: ${({ $itemSize }) => `${$itemSize}px`};
  background-color: ${({ $color }) => $color};
  border: ${({ theme, $isSelected }) =>
    $isSelected ? `2px solid ${theme.colors.white}` : `2px solid transparent`};
  transition: transform 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: scale(1.1);
  }
`;

const MAX_WIDTH = 360;
const PADDING_HORIZONTAL = 16;
const COLORS_IN_ROW = 12;

type Props = {
  currentColor: string;
  isColorPickerModalShown: boolean;
  hideColorPickerModal: () => void;
  onSelectColor: (color: string) => void;
};

const ColorPickerModal: FC<Props> = ({
  currentColor,
  isColorPickerModalShown,
  hideColorPickerModal,
  onSelectColor,
}) => {
  const [selectedColor, setSelectedColor] = useState<string>(currentColor);
  const { width } = useWindowDimensions();
  const calculatedWidth = useMemo(
    () => Math.floor(width - PADDING_HORIZONTAL * 2),
    [width],
  );
  const containerWidth = useMemo(() => {
    const rawWidth = calculatedWidth > MAX_WIDTH ? MAX_WIDTH : calculatedWidth;
    return Math.floor(rawWidth / COLORS_IN_ROW) * COLORS_IN_ROW;
  }, [calculatedWidth]);
  const itemSize = useMemo(
    () => Math.floor(containerWidth / COLORS_IN_ROW),
    [containerWidth],
  );

  const handleSelectColor = useCallback((hex: string) => {
    setSelectedColor(hex);
  }, []);

  const onClose = useCallback(() => {
    setSelectedColor(currentColor);
    hideColorPickerModal();
  }, [currentColor, hideColorPickerModal]);

  const onSubmit = useCallback(() => {
    onSelectColor(selectedColor);
    hideColorPickerModal();
  }, [onSelectColor, selectedColor, hideColorPickerModal]);

  return (
    <Modal
      isShown={isColorPickerModalShown}
      onClose={onClose}
      title='Choose Username Color'
      isSkewed={false}
      isActionable
      onConfirm={onSubmit}
    >
      <Container $containerWidth={containerWidth}>
        {theme.colorsPalette.map((color) => (
          <ColorButton
            key={color}
            $color={color}
            $isSelected={color === selectedColor}
            $itemSize={itemSize}
            onClick={() => handleSelectColor(color)}
          />
        ))}
      </Container>
    </Modal>
  );
};

export default memo(ColorPickerModal);
